import React, { useState } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import Sidebar from './Sidebar';
import MobileNav from './MobileNav';
import TopAppBar from './TopAppBar';
import { LimitedAccessBanner } from '../shared/LimitedAccessBanner';
import { TrialBanner } from '../shared/TrialBanner';
import { RenewalBanner } from '../shared/RenewalBanner';
import { BillingGate } from '../shared/BillingGate';
import { SuspendedGate } from '../shared/SuspendedGate';
import { ImpersonationBanner } from '../shared/ImpersonationBanner';

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Super-admin "view as user" banner (fixed, top of everything) */}
      <ImpersonationBanner />

      {/* Desktop Sidebar */}
      {!isMobile && <Sidebar open={sidebarOpen} onToggle={handleSidebarToggle} />}

      {/* Main Content Area */}
      <Box
        component="main"
        id="main-content"
        tabIndex={-1}
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          // Flex items default to `min-width: auto`, so a page with wide content
          // (a data table, a code block) grew this column past the viewport and
          // scrolled the sidebar off-screen. Allow it to shrink; wide content
          // scrolls inside its own container instead.
          minWidth: 0,
          minHeight: '100vh',
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          '&:focus': {
            outline: 'none',
          },
          ...(isMobile && {
            // Clear the fixed 64px bottom nav plus the iOS home-indicator area.
            pb: 'calc(64px + env(safe-area-inset-bottom) + 8px)',
          }),
        }}
      >
        {/* Top App Bar */}
        <TopAppBar onMenuClick={handleSidebarToggle} />

        {/* Limited Access Banner */}
        <LimitedAccessBanner />

        {/* Free-trial countdown + one-time-license renewal (org admins) */}
        <TrialBanner />
        <RenewalBanner />

        {/* Page Content */}
        <Box
          sx={{
            flexGrow: 1,
            minWidth: 0, // see the note on the main column above
            p: { xs: 2, sm: 3 },
            mt: 8, // Account for fixed app bar height
          }}
        >
          <SuspendedGate>
            <BillingGate>{children}</BillingGate>
          </SuspendedGate>
        </Box>
      </Box>

      {/* Mobile Bottom Navigation */}
      {isMobile && <MobileNav />}
    </Box>
  );
};

export default AppLayout;
